import React, { useState, useEffect } from "react";
import { Card, Form, Button, Table, Badge, Spinner, Alert, Row, Col } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../services/api";

const DaftarPengajuanReguler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Restore state from navigation if available
  const restoredState = location.state?.filterState;
  
  const [tglAwal, setTglAwal] = useState(restoredState?.tglAwal || "");
  const [tglAkhir, setTglAkhir] = useState(restoredState?.tglAkhir || "");
  const [searchText, setSearchText] = useState(restoredState?.searchText || "");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(restoredState?.total || 0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Helper function to format date as yyyy-mm-dd in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Set default date range to 1st of current month - today (only if no restored state)
  useEffect(() => {
    if (!restoredState) {
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      setTglAwal(formatDateLocal(startOfMonth));
      setTglAkhir(formatDateLocal(today));
    }
  }, [restoredState]);

  const fetchData = async () => {
    if (!tglAwal || !tglAkhir) {
      setError("Tanggal awal dan akhir harus diisi");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API_URL}/reguler/sertifikat-reguler?tgl_awal=${tglAwal}&tgl_akhir=${tglAkhir}`
      );

      if (response.data.success) {
        setData(response.data.data || []);
        setFilteredData(response.data.data || []);
        setTotal(response.data.total || 0);
      } else {
        setError(response.data.message || "Gagal mengambil data");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.response?.data?.message || "Terjadi kesalahan saat mengambil data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      "OF300": { variant: "success", label: "Terbit SH" },
      "OF200": { variant: "warning", label: "Proses" },
      "OF100": { variant: "info", label: "Pendaftaran" },
    };

    const statusInfo = statusMap[status] || { variant: "secondary", label: status };

    return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Filter data based on search text
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredData(data);
      return;
    }

    const searchLower = searchText.toLowerCase();
    const filtered = data.filter((item) =>
      (item.no_daftar?.toLowerCase() || "").includes(searchLower) ||
      (item.nama_pu?.toLowerCase() || "").includes(searchLower) ||
      (item.email?.toLowerCase() || "").includes(searchLower) ||
      (item.prov_pu?.toLowerCase() || "").includes(searchLower) ||
      (item.kota_pu?.toLowerCase() || "").includes(searchLower) ||
      (item.merek_dagang?.toLowerCase() || "").includes(searchLower) ||
      (item.status_registrasi?.toLowerCase() || "").includes(searchLower)
    );
    setFilteredData(filtered);
  }, [searchText, data]);

  // Sorting function
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[key] || "";
      const bValue = b[key] || "";

      if (typeof aValue === "string" && typeof bValue === "string") {
        return direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return direction === "asc"
        ? (aValue > bValue ? 1 : -1)
        : (aValue < bValue ? 1 : -1);
    });

    setFilteredData(sorted);
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort text-muted opacity-50 ms-1"></i>;
    }
    return sortConfig.direction === "asc"
      ? <i className="fas fa-sort-up text-primary ms-1"></i>
      : <i className="fas fa-sort-down text-primary ms-1"></i>;
  };

  return (
    <div className="container-fluid py-4">
      <h4 className="mb-4">
        <i className="fas fa-list me-2"></i>
        Daftar Pengajuan Reguler
      </h4>

      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Tanggal Awal</Form.Label>
                <Form.Control
                  type="date"
                  value={tglAwal}
                  onChange={(e) => setTglAwal(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Tanggal Akhir</Form.Label>
                <Form.Control
                  type="date"
                  value={tglAkhir}
                  onChange={(e) => setTglAkhir(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Cari</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Cari no daftar, nama, email..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button
                variant="primary"
                onClick={fetchData}
                disabled={loading}
                className="w-100"
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Memuat...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2"></i>
                    Tampilkan Data
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">
              Total Data: <Badge bg="primary">{total}</Badge>
              {searchText && (
                <span className="ms-2 text-muted">
                  (Difilter: <Badge bg="info">{filteredData.length}</Badge>)
                </span>
              )}
            </h6>
          </div>

          <div className="table-responsive">
            <Table striped bordered hover className="mb-0" style={{ fontSize: "0.85rem" }}>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "50px", cursor: "pointer" }} onClick={() => handleSort("index")}>No</th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("no_daftar")}>
                    No. Daftar {getSortIcon("no_daftar")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("tgl_daftar")}>
                    Tanggal Daftar {getSortIcon("tgl_daftar")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("nama_pu")}>
                    Nama PU {getSortIcon("nama_pu")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("status_reg")}>
                    Status {getSortIcon("status_reg")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("skala_usaha")}>
                    Skala Usaha {getSortIcon("skala_usaha")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("jenis_layanan")}>
                    Jenis Layanan {getSortIcon("jenis_layanan")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("jenis_produk")}>
                    Jenis Produk {getSortIcon("jenis_produk")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("merek_dagang")}>
                    Merek Dagang {getSortIcon("merek_dagang")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("prov_pu")}>
                    Provinsi {getSortIcon("prov_pu")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("kota_pu")}>
                    Kota {getSortIcon("kota_pu")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("email")}>
                    Email {getSortIcon("email")}
                  </th>
                  <th style={{ width: "100px" }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="13" className="text-center py-4">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2 text-muted">Memuat data...</p>
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="text-center py-4 text-muted">
                      <i className="fas fa-inbox fa-2x mb-2"></i>
                      <p>
                        {searchText
                          ? "Tidak ada data yang cocok dengan pencarian"
                          : "Belum ada data untuk ditampilkan"}
                      </p>
                      <p className="small">
                        {searchText
                          ? "Coba ubah kata kunci pencarian"
                          : "Silakan pilih rentang tanggal dan klik \"Tampilkan Data\""}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, index) => (
                    <tr key={item.id_reg}>
                      <td>{index + 1}</td>
                      <td>{item.no_daftar}</td>
                      <td>{formatDate(item.tgl_daftar)}</td>
                      <td>{item.nama_pu}</td>
                      <td>{getStatusBadge(item.status_reg)}</td>
                      <td>{item.skala_usaha}</td>
                      <td>{item.jenis_layanan}</td>
                      <td>{item.jenis_produk}</td>
                      <td>{item.merek_dagang}</td>
                      <td>{item.prov_pu}</td>
                      <td>{item.kota_pu}</td>
                      <td>{item.email}</td>
                      <td>
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() => {
                            // Save current filter state to pass to detail page
                            const filterState = {
                              tglAwal,
                              tglAkhir,
                              searchText,
                              data,
                              filteredData,
                              total,
                            };
                            navigate(`/admin/reguler/detail/${item.id_reg}`, {
                              state: { filterState },
                            });
                          }}
                          title="Lihat Detail"
                        >
                          <i className="fas fa-eye"></i> Detil
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DaftarPengajuanReguler;
